import React from 'react';
import classNames from 'classnames';
import { Provider, useStore } from 'react-redux';
import { OverlayView } from 'react-google-maps';

import SVG from 'components/base/SVG';
import SubjectPin from 'assets/images/investor-subject-pin.svg';

import {
  getInvestorTypeColor,
  getInvestorTypeIcon,
  getPrimaryInvestorType,
} from '../investorTypeTheme';
import InvestorInfoPopover from '../InvestorInfoPopover';
import css from './style.scss';

// One property pin — Figma "Map Pins" (231:8427): a 28.184 x 33.683 teardrop
// with a 20px white disc at (4,4) and the type glyph at (6,6).
//
// Rendered through OverlayView rather than a Marker with an image icon, so the
// pin body and glyph can come from the SVG sprite and be tinted from the
// classification colour in CSS. A Marker icon would need a rasterised or
// data-URI image per colour, which cannot reference the page's sprite.
//
// The pin's tip marks the coordinate, so the overlay is offset by half its
// width and its full height.
const getPixelPositionOffset = (width, height) => ({
  x: -(width / 2),
  y: -height,
});

// Which pin is open is owned by the map, not the marker, so only one card can
// ever be visible.
//
// Opened by clicking the pin — clicking it again, clicking another pin, clicking
// the map, or the card's own close button dismisses it.
// Colour and glyph are resolved here rather than on the pin itself: the pin is
// built in the data layer, which stays free of presentation, and the design
// gives the map pin the same per-classification theme as the card badge.
const InvestorMarker = ({ pin, active, onSelect, onClose }) => {
  const type = getPrimaryInvestorType(pin.investor && pin.investor.classifications) || pin.type;
  const color = getInvestorTypeColor(type);
  const icon = getInvestorTypeIcon(type);

  // react-google-maps mounts OverlayView children with
  // ReactDOM.unstable_renderSubtreeIntoContainer, i.e. a SEPARATE React root.
  // That API only ever carried legacy context, so nothing built on
  // React.createContext crosses into it -- and react-redux 8 is built on
  // createContext. Anything in here that touches the store (the popover's
  // useSaveInvestor -> useDispatch) therefore throws "could not find
  // react-redux context value". Grab the store out here, where we are still
  // inside the app's Provider, and re-provide it across the boundary. It is
  // the same store instance, so dispatches still reach the real PopupHolder.
  const store = useStore();

  return (
    <OverlayView
      position={pin.position}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      getPixelPositionOffset={getPixelPositionOffset}
    >
      <Provider store={store}>
        <div className={classNames(css.marker, { [css.markerActive]: active })}>
          <div
            className={css.pin}
            style={{ color }}
            role="button"
            tabIndex={0}
            title={pin.investorName || undefined}
            // Google's map click fires from its own hit testing, but stop the DOM
            // event anyway so a pin click can never also read as a map click and
            // immediately dismiss the card it just opened.
            onClick={(e) => {
              e.stopPropagation();
              if (onSelect) onSelect(pin);
            }}
            onKeyPress={(e) => {
              if (e.key !== 'Enter' && e.key !== ' ') return;
              e.preventDefault();
              if (onSelect) onSelect(pin);
            }}
          >
            {pin.isSubject ? (
              // The searched property — the star pin (Figma 231:8427), a single
              // full-colour SVG rather than the tinted sprite body + classification
              // glyph the investor pins use.
              <img src={SubjectPin} alt="" className={css.pin__subject} />
            ) : (
              <>
                <SVG icon="iconInvestorMapPin" className={css.pin__body} />
                <span className={css.pin__disc} />
                {!icon ? null : <SVG icon={icon} className={css.pin__glyph} />}
              </>
            )}
          </div>
          {!active ? null : (
            <div className={css.popover}>
              <InvestorInfoPopover pin={pin} onClose={onClose} />
            </div>
          )}
        </div>
      </Provider>
    </OverlayView>
  );
};

export default InvestorMarker;
